import { motion, AnimatePresence, useInView } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import {
    Bot, Code, MessageSquare, Zap, ArrowRight, 
    CheckCircle, Star, Globe, Shield, BarChart3,
    Users, Clock, Sparkles, X, Brain, Mic
} from 'lucide-react'
import LandingHeader from '../../components/landing/LandingHeader'
import LandingFooter from '../../components/landing/LandingFooter'
import { getSubscriptionPlans } from '../../services/subscription_plan'

const Products = () => {
    const navigate = useNavigate()
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [selectedProductForPlans, setSelectedProductForPlans] = useState(null)

    const handleGetStarted = () => {
        navigate('/signin')
    }

    const handleProductClick = (product) => {
        if (product.id === 'talkida-ai' || product.id === 'talkida-help-support' || product.id === 'talkida-voice') {
            setSelectedProduct(product)
        }
    }

    const handleViewPlans = (product) => {
        setSelectedProductForPlans(product)
    }

    const closeProductDetails = () => {
        setSelectedProduct(null)
    }

    const closePlansModal = () => {
        setSelectedProductForPlans(null)
    }

    const products = [
        {
            id: 'talkida-ai',
            name: 'Talkida AI Assistant',
            tagline: 'Intelligent Conversational AI',
            description: 'Enterprise-grade AI assistant that integrates seamlessly into any website. Deliver instant customer support, capture qualified leads, and engage visitors with intelligent conversations 24/7.',
            icon: Bot,
            status: 'Available',
            features: [
                'Plug-and-play integration in 5 minutes',
                'Advanced AI-powered conversations',
                'Lead capture & qualification',
                'Multi-language support (100+ languages)',
                'Custom branding & theming',
                'Real-time analytics & insights'
            ],
            pricing: 'Starting at ₹999/month',
            link: '/#features'
        },
        {
            id: 'talkida-help-support',
            name: 'Talkida AI Help & Support',
            tagline: 'Complete Customer Support Solution',
            description: 'Comprehensive help desk platform with AI-first approach. Seamlessly escalate from AI bot to human agents, manage tickets, book appointments, and provide enterprise-grade customer support.',
            icon: Users,
            status: 'Coming Soon',
            features: [
                'Automatic ticket generation from conversations',
                'Monitor your support team from dashboard',
                'AI bot to human agent escalation',
                'Real-time ticket status tracking',
                'Team performance monitoring',
                'Customer satisfaction metrics'
            ],
            pricing: 'Starting at ₹2,999/month',
            link: '#'
        },
        {
            id: 'talkida-voice',
            name: 'Talkida Voice Intelligence',
            tagline: 'Voice-Enabled AI Platform',
            description: 'Next-generation voice AI platform for websites and mobile applications. Enable natural voice interactions, voice commands, and advanced speech processing capabilities.',
            icon: Mic,
            status: 'Coming Soon',
            features: [
                'Natural voice conversations',
                'Advanced speech-to-text processing',
                'Voice command recognition',
                'Multi-accent & language support',
                'Real-time voice analytics',
                'Voice biometric authentication'
            ],
            pricing: 'Starting at ₹1,999/month',
            link: '#'
        }
    ]

    return (
        <>
            <Helmet>
                <title>Products - Talkida Technologies | AI Solutions Portfolio</title>
                <meta name="description" content="Explore Talkida's comprehensive suite of AI products including chatbots, voice assistants, analytics, and enterprise solutions. Transform your business with intelligent AI technology." />
                <meta name="keywords" content="AI products, chatbot solutions, voice assistant, analytics platform, enterprise AI, Talkida products" />
            </Helmet>

            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
                <LandingHeader onGetStarted={handleGetStarted} />
                
                {/* Hero Section */}
                <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                                Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Products</span>
                            </h1>
                            <p className="text-xl text-white/70 max-w-2xl mx-auto">
                                Discover our comprehensive suite of AI-powered solutions designed to transform your business and enhance customer experiences.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Products Grid */}
                <section className="pb-20 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {products.map((product, index) => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="group relative"
                                >
                                    <div className="relative bg-gray-800 border border-gray-700 rounded-2xl p-8 hover:bg-gray-700 transition-all duration-300 h-full flex flex-col">
                                        {/* Status Badge */}
                                        <div className="absolute top-4 right-4">
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                product.status === 'Available' 
                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                                    : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                                            }`}>
                                                {product.status}
                                            </span>
                                        </div>

                                        {/* Product Icon */}
                                        <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center mb-6">
                                            <product.icon className="w-8 h-8 text-white" />
                                        </div>

                                        {/* Product Info */}
                                        <div className="mb-6">
                                            <h3 className="text-2xl font-bold text-white mb-2">{product.name}</h3>
                                            <p className="text-blue-400 font-semibold mb-4">{product.tagline}</p>
                                            <p className="text-white/70 leading-relaxed">{product.description}</p>
                                        </div>

                                        {/* Features */}
                                        <div className="mb-6 flex-grow">
                                            <h4 className="text-white font-semibold mb-3">Key Features:</h4>
                                            <ul className="space-y-2">
                                                {product.features.slice(0, 6).map((feature, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-white/70">
                                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                                        <span className="text-sm">{feature}</span>
                                                    </li>
                                                ))}
                                                {product.features.length > 6 && (
                                                    <li className="text-white/50 text-sm italic">
                                                        +{product.features.length - 6} more features...
                                                    </li>
                                                )}
                                            </ul>
                                        </div>

                                        {/* Pricing & CTA - Always at bottom */}
                                        <div className="mt-auto pt-4">
                                            <div className="mb-4 flex items-center justify-between">
                                                <span className="text-white font-semibold">
                                                    {product.status === 'Available' ? product.pricing : 'Pricing Coming Soon'}
                                                </span>
                                                <div className="flex items-center gap-4">
                                                    {product.status === 'Available' ? (
                                                        <button
                                                            onClick={() => window.open('https://cdn.talkida.co.in/', '_blank')}
                                                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium group"
                                                            title="View documentation"
                                                        >
                                                            View Docs
                                                            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                                                        </button>
                                                    ) : (
                                                        <span className="flex items-center gap-1 text-gray-500 text-sm font-medium cursor-not-allowed" title="Documentation not available yet">
                                                            View Docs
                                                            <ArrowRight className="w-3 h-3" />
                                                        </span>
                                                    )}
                                                    {product.status === 'Available' ? (
                                                        <button
                                                            onClick={() => handleViewPlans(product)}
                                                            className="flex items-center gap-1 text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium group"
                                                            title="View pricing plans"
                                                        >
                                                            View Plans
                                                            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                                                        </button>
                                                    ) : (
                                                        <span className="flex items-center gap-1 text-gray-500 text-sm font-medium cursor-not-allowed" title="Plans not available yet">
                                                            View Plans
                                                            <ArrowRight className="w-3 h-3" />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-3">
                                                {product.status === 'Available' ? (
                                                    <>
                                                        <button
                                                            onClick={() => handleProductClick(product)}
                                                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-300"
                                                        >
                                                            View Features
                                                            <ArrowRight className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={handleGetStarted}
                                                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white font-semibold rounded-xl transition-all duration-300"
                                                        >
                                                            Get Started Free
                                                        </button>
                                                    </>
                                                ) : product.id === 'talkida-help-support' ? (
                                                    <>
                                                        <button
                                                            onClick={() => handleProductClick(product)}
                                                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-300"
                                                        >
                                                            View Features
                                                            <ArrowRight className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            disabled
                                                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white/50 font-semibold rounded-xl cursor-not-allowed"
                                                        >
                                                            <Clock className="w-4 h-4" />
                                                            Coming Soon
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => handleProductClick(product)}
                                                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-300"
                                                        >
                                                            View Features
                                                            <ArrowRight className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            disabled
                                                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white/50 font-semibold rounded-xl cursor-not-allowed opacity-50"
                                                        >
                                                            <Clock className="w-4 h-4" />
                                                            Coming Soon
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-20 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="bg-gray-800 border border-gray-700 rounded-3xl p-12"
                        >
                            <Sparkles className="w-16 h-16 text-blue-400 mx-auto mb-6" />
                            <h2 className="text-4xl font-bold text-white mb-6">
                                Ready to Transform Your Business?
                            </h2>
                            <p className="text-xl text-white/70 mb-8">
                                Start with our flagship Talkida AI Chatbot and experience the power of intelligent customer engagement.
                            </p>
                            <button
                                onClick={handleGetStarted}
                                className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full transition-all duration-300"
                            >
                                Start Free Trial
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </motion.div>
                    </div>
                </section>

                <LandingFooter />
            </div>

            {/* Product Details Modal */}
            <AnimatePresence>
                {selectedProduct && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={closeProductDetails}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="flex items-start justify-between mb-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                                        <selectedProduct.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold text-white">{selectedProduct.name}</h2>
                                        <p className="text-blue-400 font-medium">{selectedProduct.tagline}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeProductDetails}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    <X className="w-6 h-6 text-white/60" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="space-y-8">
                                {/* Description */}
                                <div>
                                    <h3 className="text-xl font-semibold text-white mb-3">Overview</h3>
                                    <p className="text-white/70 leading-relaxed">{selectedProduct.description}</p>
                                </div>

                                {/* Features */}
                                <div>
                                    <h3 className="text-2xl font-semibold text-white mb-6 text-center">
                                        Everything You Need, <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Out of the Box</span>
                                    </h3>
                                    <p className="text-white/70 text-center mb-8 max-w-2xl mx-auto">
                                        Powerful features that make Talkida the easiest and most comprehensive chatbot platform
                                    </p>
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {selectedProduct.features.map((feature, index) => {
                                            const featureIcons = [Zap, Brain, Code, Globe, Shield, BarChart3];
                                            const gradients = [
                                                'from-yellow-400 to-orange-500',
                                                'from-purple-400 to-pink-500', 
                                                'from-blue-400 to-cyan-500',
                                                'from-green-400 to-emerald-500',
                                                'from-red-400 to-pink-500',
                                                'from-indigo-400 to-purple-500'
                                            ];
                                            const FeatureIcon = featureIcons[index % featureIcons.length];
                                            const gradient = gradients[index % gradients.length];
                                            
                                            return (
                                                <div key={index} className="bg-white/5 backdrop-blur-md p-6 rounded-2xl border border-white/10 hover:border-purple-500/50 transition-all duration-300 group">
                                                    <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                                        <FeatureIcon className="w-6 h-6 text-white" />
                                                    </div>
                                                    <h4 className="text-lg font-semibold text-white mb-2">
                                                        {feature.split(' - ')[0] || feature.split(':')[0] || feature}
                                                    </h4>
                                                    <p className="text-white/60 text-sm leading-relaxed">
                                                        {feature.split(' - ')[1] || feature.split(':')[1] || 'Advanced feature for enhanced functionality'}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Pricing */}
                                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-6 border border-blue-500/20">
                                    <h3 className="text-xl font-semibold text-white mb-2">Pricing</h3>
                                    <p className="text-2xl font-bold text-blue-400 mb-4">{selectedProduct.pricing}</p>
                                    <button
                                        onClick={handleGetStarted}
                                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
                                    >
                                        Get Started Free
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Plans Modal */}
            <AnimatePresence>
                {selectedProductForPlans && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={closePlansModal}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="flex items-start justify-between mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center">
                                        <selectedProductForPlans.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-3xl font-bold text-white">{selectedProductForPlans.name}</h2>
                                        <p className="text-purple-400 font-medium">Pricing Plans</p>
                                    </div>
                                </div>
                                <button
                                    onClick={closePlansModal}
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    <X className="w-6 h-6 text-white/60" />
                                </button>
                            </div>

                            <PricingSection 
                                productName={selectedProductForPlans.name}
                                onGetStarted={handleGetStarted} 
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )


}

// Pricing Section Component
const PricingSection = ({ productName, onGetStarted }) => {
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { once: true, amount: 0.3 })
    const [plans, setPlans] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)

    // Helper function to convert plan names to proper case
    const formatPlanName = (name) => {
        if (!name) return ''
        return name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
    }

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                setIsLoading(true)
                setError(null)
                const response = await getSubscriptionPlans()

                if (response.error) {
                    setError(response.msg);
                    setPlans([])
                    return
                }

                const sortedPlans = response.data
                    .map(plan => ({
                        ...plan,
                        is_popular: plan.name === 'PROFESSIONAL'
                    }))
                    .sort((a, b) => {
                        const order = { 'FREE': 1, 'BASIC': 2, 'PROFESSIONAL': 3, 'ENTERPRISE': 4 }
                        return (order[a.name] || 999) - (order[b.name] || 999)
                    })

                setPlans(sortedPlans)
            } catch (err) {
                setError(`Failed to load pricing plans: ${err.message}`)
                setPlans([])
            } finally {
                setIsLoading(false)
            }
        }

        fetchPlans()
    }, [])

    return (
        <section ref={sectionRef} className="py-8">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                >
                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                        {productName} <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">Plans</span>
                    </h3>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto">
                        Choose the perfect plan for your needs. Start free and scale as you grow.
                    </p>
                </motion.div>

                {isLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="text-white/60">Loading pricing plans...</div>
                    </div>
                ) : error ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="text-red-400">{error}</div>
                    </div>
                ) : plans.length === 0 ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="text-white/60">No pricing plans available</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto pb-4 pt-8">
                        <div className="flex gap-6 min-w-max items-stretch justify-center">
                            {plans.map((plan, index) => (
                                <motion.div
                                    key={plan.id}
                                    className="relative flex-shrink-0 w-80 flex"
                                    initial={{ opacity: 0, y: 50 }}
                                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    {plan.is_popular && (
                                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-2 rounded-full text-white text-sm font-semibold z-20 whitespace-nowrap shadow-lg">
                                            Most Popular
                                        </div>
                                    )}

                                    <div className={`bg-white/5 backdrop-blur-md p-8 rounded-2xl border ${plan.is_popular ? 'border-purple-500' : 'border-white/10'} hover:border-purple-500/50 transition-all duration-300 flex flex-col justify-between w-full ${plan.is_popular ? 'scale-105' : ''}`}>
                                        <div>
                                            <h4 className="text-2xl font-bold text-white mb-2">{formatPlanName(plan.name)}</h4>
                                            <p className="text-white/60 mb-6">{plan.description}</p>

                                            <div className="mb-6">
                                                <span className="text-4xl font-bold text-white">
                                                    {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString('en-IN')}`}
                                                </span>
                                                {plan.duration_days && (
                                                    <span className="text-white/60 ml-2">/ {plan.duration_days} days</span>
                                                )}
                                            </div>

                                            <ul className="space-y-3 mb-8">
                                                {plan.features.map((feature, i) => (
                                                    <li key={i} className="flex items-start gap-2 text-white/80">
                                                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                                        <span className="text-sm leading-relaxed">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <button
                                            onClick={onGetStarted}
                                            className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${plan.is_popular
                                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/50'
                                                : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                                                }`}
                                        >
                                            Get Started
                                        </button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}

export default Products